import React from "react";
import { saveAs } from "file-saver";
import { utils, write } from "xlsx";
import { Button } from "@mui/material";
import { Download } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const isDate = (value) =>
  Object.prototype.toString.call(value) === "[object Date]" &&
  !Number.isNaN(value.getTime());

const formatExportDate = (date) =>
  date.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

const getStudyLabel = (study) => {
  if (!study || typeof study !== "object") {
    return study ?? "";
  }

  return (
    [study.cdlStudyCode, study.sponsorStudyCode].filter(Boolean).join(" / ") ||
    study.name ||
    study.reference ||
    study.id ||
    ""
  );
};

const getCenterLabel = (center) => {
  if (!center || typeof center !== "object") {
    return center ?? "";
  }

  const centerName = [
    center.centerNumber,
    center.hospital || center.city || center.country,
  ]
    .filter(Boolean)
    .join(" - ");
  const studyLabel = getStudyLabel(center.Study || center.study);

  return [centerName || center.id, studyLabel ? `(${studyLabel})` : ""]
    .filter(Boolean)
    .join(" ");
};

const getObjectLabel = (value) => {
  if (!value || typeof value !== "object") {
    return value ?? "";
  }

  if (value.cdlStudyCode || value.sponsorStudyCode) {
    return getStudyLabel(value);
  }

  if (value.centerNumber || value.hospital || value.city) {
    return getCenterLabel(value);
  }

  if (value.firstname || value.name) {
    return [value.firstname, value.name].filter(Boolean).join(" ");
  }

  if (value.reference || value.code || value.label || value.id) {
    return value.reference || value.code || value.label || value.id;
  }

  const primitiveEntries = Object.entries(value)
    .filter(([, item]) => item == null || typeof item !== "object")
    .map(([key, item]) => `${key}: ${item ?? ""}`);

  return primitiveEntries.length > 0
    ? primitiveEntries.join("; ")
    : JSON.stringify(value);
};

const formatCellValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (isDate(value)) {
    return { v: value, t: "d" };
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        const formattedItem = formatCellValue(item);

        return formattedItem && typeof formattedItem === "object"
          ? formattedItem.v
          : formattedItem;
      })
      .join(", ");
  }

  if (typeof value === "object") {
    return getObjectLabel(value);
  }

  return value;
};

const capitalizeHeader = (header) =>
  header ? `${header.charAt(0).toUpperCase()}${header.slice(1)}` : "";

const formatHeader = (header, columnLabels, capitalizeHeaders) => {
  if (columnLabels[header]) {
    return columnLabels[header];
  }

  return capitalizeHeaders ? capitalizeHeader(header) : header;
};

const stringToArrayBuffer = (value) => {
  const buffer = new ArrayBuffer(value.length);
  const view = new Uint8Array(buffer);

  for (let index = 0; index < value.length; index += 1) {
    view[index] = value.charCodeAt(index) & 0xff;
  }

  return buffer;
};

export const downloadSpreadsheet = ({
  data,
  filename,
  includeExportMetadata = false,
  excludedColumns = [],
  columnLabels = {},
  capitalizeHeaders = false,
}) => {
  if (!Array.isArray(data) || data.length === 0) {
    return;
  }

  const excludedColumnSet = new Set(excludedColumns);
  const headers = Object.keys(data[0]).filter(
    (header) => !excludedColumnSet.has(header),
  );
  const displayHeaders = headers.map((header) =>
    formatHeader(header, columnLabels, capitalizeHeaders),
  );
  const aoaData = includeExportMetadata
    ? [
        ["CDL Portal", `Export date: ${formatExportDate(new Date())}`],
        displayHeaders,
      ]
    : [displayHeaders];

  data.forEach((row) => {
    const rowData = headers.map((header) => formatCellValue(row[header]));
    aoaData.push(rowData);
  });

  const worksheet = utils.aoa_to_sheet(aoaData);
  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, worksheet, "Sheet1");

  const workbookOutput = write(workbook, { bookType: "xlsx", type: "binary" });
  saveAs(
    new Blob([stringToArrayBuffer(workbookOutput)], {
      type: "application/octet-stream",
    }),
    `${filename}.xlsx`,
  );
};

const CSVDownloader = ({
  data,
  filename,
  name,
  includeExportMetadata = false,
  excludedColumns = [],
  columnLabels = {},
  capitalizeHeaders = false,
}) => {
  const { t } = useTranslation();
  const handleDownload = () => {
    downloadSpreadsheet({
      data,
      filename,
      includeExportMetadata,
      excludedColumns,
      columnLabels,
      capitalizeHeaders,
    });
  };

  return (
    <Button
      onClick={handleDownload}
      variant="contained"
      color="primary"
      style={{ height: 56, padding: "0 28px", minWidth: 180, margin: 0 }}
      startIcon={<Download />}
    >
      {t("download")} {name}
    </Button>
  );
};

export default CSVDownloader;
